import { checkRequiredQTY, shouldAddItemsToCart } from 'src/utils/util';

/**
 * Normalize a CFN value for consistent map-key lookups.
 */
export const normalizeCFN = (value) => String(value ?? '').trim().toUpperCase();

/**
 * Sum total cart quantity for a given CFN (includes auto-added items).
 */
export const sumCartQtyByCFN = (products, cfn) => {
    const key = normalizeCFN(cfn);
    if (!key) return 0;
    return (products ?? []).reduce((sum, item) => {
        const itemKey = normalizeCFN(item?.productCFN ?? item?.cfn);
        if (itemKey !== key) return sum;
        return sum + (Number(item?.quantity) || 0);
    }, 0);
};

/**
 * Sum only "user-added" (non-auto) parent quantity for a CFN.
 * Auto-added items (isAddItemRuleProduct=true) are excluded to prevent
 * circular rules (A<->B) from spiralling quantities.
 */
export const sumUserParentQtyByCFN = (products, cfn) => {
    const key = normalizeCFN(cfn);
    if (!key) return 0;
    return (products ?? []).reduce((sum, item) => {
        const itemKey = normalizeCFN(item?.productCFN ?? item?.cfn);
        if (itemKey !== key) return sum;
        if (item?.isAddItemRuleProduct === true) {
            return sum + (Number(item?.userAddedQuantity) || 0);
        }
        return sum + (Number(item?.quantity) || 0);
    }, 0);
};

/**
 * Build a Map of CFN -> total quantity from cart items.
 */
export const deriveCartTotalsByCFN = (cartItems) => {
    const totals = new Map();
    for (const item of cartItems || []) {
        if (!item || !item.productCFN) continue;
        const cfn = normalizeCFN(item.productCFN);
        const qty = Number(item.quantity || 0);
        totals.set(cfn, (totals.get(cfn) || 0) + qty);
    }
    return totals;
};

/**
 * Reconcile addedItemsState with the current cart snapshot.
 * Detects added quantities (positive deltas) and updates the tracking state.
 */
export const buildSyncedItemsState = (cartItems, prevState) => {
    const totalsByCFN = deriveCartTotalsByCFN(cartItems);
    let next = { ...prevState };

    const allCFNs = new Set([
        ...Object.keys(prevState || {}),
        ...totalsByCFN.keys(),
    ]);

    if (cartItems.length > 0) {
        for (const cfn of allCFNs) {
            const prevNode = prevState?.[cfn] || { addedQuantity: 0, cartQuantity: 0 };
            const prevCartQty = Number(prevNode.cartQuantity || 0);
            const prevAddedQty = Number(prevNode.addedQuantity || 0);
            const newCartQty = Number(totalsByCFN.get(cfn) || 0);
            const deltaAdd = Math.max(0, newCartQty - prevCartQty);
            next[cfn] = {
                addedQuantity: prevAddedQty + deltaAdd,
                cartQuantity: newCartQty,
            };
        }
    } else {
        next = {};
    }
    return next;
};

/**
 * Build a CFN -> quantity map from cart products.
 */
export const buildCartItemMap = (products) => {
    return (products ?? []).reduce((acc, item) => {
        const key = normalizeCFN(item?.productCFN ?? item?.cfn);
        if (!key) return acc;
        const qty = Number(item?.quantity) || 0;
        acc[key] = (acc[key] ?? 0) + qty;
        return acc;
    }, {});
};

/**
 * Deduplicate addItemRules so identical rules from multiple cart lines
 * don't cause double-counting of missing quantities.
 */
export const dedupeRules = (rulesInput) => {
    const dedupedRulesMap = new Map();
    for (const rawRule of rulesInput) {
        const materialAKey = normalizeCFN(rawRule?.materialA);
        const materialBKey = normalizeCFN(rawRule?.materialB);
        const materialAQty = String(rawRule?.materialAQty ?? '').trim();
        const materialBQty = String(rawRule?.materialBQty ?? '').trim();
        const qualifierKey = normalizeCFN(rawRule?.setMaterialQualifier);
        const ruleKey = `${materialAKey}|${materialAQty}|${materialBKey}|${materialBQty}|${qualifierKey}`;
        if (!dedupedRulesMap.has(ruleKey)) {
            dedupedRulesMap.set(ruleKey, {
                ...rawRule,
                materialA: materialAKey,
                materialB: materialBKey,
                setMaterialQualifier: qualifierKey || rawRule?.setMaterialQualifier,
            });
        }
    }
    return Array.from(dedupedRulesMap.values());
};

/**
 * Derive the list of rules to evaluate from cart products, falling back to
 * the prop-level addItemRules when no cart-derived rules exist.
 * Rules from auto-added items (isAddItemRuleProduct=true) are excluded.
 */
export const getEffectiveRules = (cartProducts, addItemRules) => {
    const cartDerivedRules = (cartProducts ?? [])
        .filter(
            (p) =>
                p?.isAddItemRuleProduct !== true ||
                Number(p?.userAddedQuantity) > 0
        )
        .flatMap((p) => p?.addItemRules ?? []);
    return cartDerivedRules.length > 0 ? cartDerivedRules : addItemRules ?? [];
};

/**
 * Compute the list of auto-add items (Material B) that are missing in the cart
 * based on add-item rules and the current cart state.
 *
 * Returns { autoAddItems, checkResult }:
 *  - autoAddItems: array of { A, AQty, B, BQty, unit } objects for items to add
 *  - checkResult: boolean from shouldAddItemsToCart (true = rules unsatisfied)
 */
export const computeAutoAddItems = ({
    cartProducts,
    addedItemsState,
    addItemRules,
    addItemData,
}) => {
    const rulesInput = getEffectiveRules(cartProducts, addItemRules);

    if (!rulesInput || rulesInput.length === 0) {
        return { autoAddItems: [], checkResult: false };
    }

    const itemMap = buildCartItemMap(cartProducts);
    const checkResult = shouldAddItemsToCart(
        cartProducts ?? [],
        addedItemsState,
        itemMap
    );

    if (!checkResult) {
        return { autoAddItems: [], checkResult: false };
    }

    const dedupedRules = dedupeRules(rulesInput);
    const autoAddItems = [];

    // Tracks how much of the existing/pending Material B has already been
    // allocated to earlier rules that share the same Material B. This ensures
    // that when multiple distinct Material A items require the SAME Material B,
    // each Material A gets its own required quantity instead of sharing a single
    // Material B (which previously satisfied every rule pointing to it).
    const consumedBByKey = {};

    for (const rule of dedupedRules) {
        const materialAKey = normalizeCFN(rule?.materialA);
        const materialBKey = normalizeCFN(rule?.materialB);
        const qualifierKey = normalizeCFN(rule?.setMaterialQualifier);

        if (qualifierKey && (itemMap[qualifierKey] || 0) > 0) {
            continue;
        }

        const itemAQty = sumUserParentQtyByCFN(cartProducts ?? [], materialAKey);
        const handledRule = (addItemData ?? []).find(
            (storedRule) =>
                normalizeCFN(storedRule?.A) === materialAKey &&
                normalizeCFN(storedRule?.B) === materialBKey
        );
        if (Number(handledRule?.AQty) >= itemAQty) {
            continue;
        }
        const cartBQty = sumCartQtyByCFN(cartProducts, materialBKey);
        const pendingBQty =
            Number(addedItemsState?.[materialBKey]?.addedQuantity) || 0;
        const baseBQty = Math.max(cartBQty, pendingBQty);
        const credit =
            Number(addedItemsState?.[materialBKey]?.decreaseCredit) || 0;
        const totalEffectiveBQty = baseBQty + credit;

        const requiredBQty = checkRequiredQTY(
            itemAQty,
            rule.materialAQty,
            rule.materialBQty
        );

        // Only the Material B not yet claimed by other rules can satisfy this one.
        const alreadyConsumed = consumedBByKey[materialBKey] || 0;
        const availableBQty = Math.max(0, totalEffectiveBQty - alreadyConsumed);
        const consumedForThisRule = Math.min(availableBQty, requiredBQty);
        consumedBByKey[materialBKey] = alreadyConsumed + consumedForThisRule;

        const missingQty = requiredBQty - consumedForThisRule;
        if (missingQty > 0) {
            autoAddItems.push({
                A: rule.materialA,
                AQty: itemAQty,
                B: rule.materialB,
                BQty: missingQty,
                unit: rule.materialAQty,
            });
            // Reserve the Material B we are about to add so a later rule sharing
            // the same Material B does not treat it as already-satisfying.
            consumedBByKey[materialBKey] =
                consumedBByKey[materialBKey] + missingQty;
        }
    }

    return { autoAddItems, checkResult };
};

/**
 * Build the products array for the validateProducts dispatch
 * from the auto-add item list, deduplicating against already-stored rules.
 */
export const buildValidateProductsList = (itemList, addItemData) => {
    const products = [];
    (itemList ?? []).forEach((it) => {
        const existingPair = (addItemData ?? []).find(
            (pr) => pr.A === it.A && pr.B === it.B
        );
        const checkABQty = (addItemData ?? []).find(
            (pr) =>
                pr.A === it.A &&
                pr.B === it.B &&
                pr.BQty === it.BQty &&
                pr.AQty === it.AQty
        );
        if (!checkABQty) {
            products.push({
                cfn: it.B,
                quantity: Math.max(
                    0,
                    existingPair?.BQty !== undefined &&
                        existingPair.BQty !== it.BQty
                        ? it.BQty - existingPair.BQty
                        : it.BQty
                ),
                salesOrg: 'S024',
            });
        }
    });
    return products;
};

/**
 * Merge new add-item rules into the stored list, updating existing
 * entries or appending new ones.
 */
export const mergeAddItemRules = (existingRules, newItems) => {
    const tempRules = existingRules ? [...existingRules] : [];

    (newItems ?? []).forEach((it) => {
        const exactMatch = tempRules.find(
            (pr) =>
                pr.A === it.A &&
                pr.B === it.B &&
                pr.BQty === it.BQty &&
                pr.AQty === it.AQty
        );
        if (!exactMatch) {
            const existing = tempRules.find(
                (pr) => pr.A === it.A && pr.B === it.B
            );
            if (existing) {
                existing.BQty = it.BQty;
                existing.AQty = it.AQty;
            } else {
                tempRules.push(it);
            }
        }
    });
    return tempRules;
};
